import { captureEvent, captureException } from '@core/plugins/sentry';
import http from '@monolith/legacy/services/api/http';

import { deserialize } from '@monolith/legacy/services/utils/jsonapi-parser';
import { rejectOrder } from '@monolith/legacy/services/api/order';
import {
  BrandRetailerRelationshipType,
  EditedOrderItems,
  ExternalOrderTypeOptions,
  ItemsType,
  ItemUpdates,
  MasterOrderBillingItem,
  ORDER_AUTOMATION_MODAL_CONTEXT_SESSION_KEY,
  OrderAutomationModalContext,
  OrderDetail,
  OrderItemEntity,
  OrderItemEntityEventsName,
  OrderStatuses,
} from '@monolith/legacy/types/order';
import { isShippingViaAnkorstore, ShipmentMethod } from '@monolith/legacy/types/shipping/shipment-method';
import { OrderBasicDetail } from '@monolith/legacy/types/orders';
import { OrderBrandRejectionType } from '@monolith/legacy/types/order-brand-rejection';
import { Params, State } from './type';
import { isEnabled } from '@monolith/legacy/services/features';
import {
  getMasterOrder,
  GetMasterOrderParams,
  getMasterOrderUuidByInternalOrderId,
} from '@monolith/legacy/services/order/order.service';
import { getSelectedProductsVariantByUuid } from '@monolith/legacy/services/api/brand-me';
import { BillingItemType } from '@monolith/legacy/types/billing-item';
import { getFulfillmentOrdersByMasterOrderUuid } from '@monolith/legacy/services/fulfillment/api';
import { captureFulfillmentException } from '@core/plugins/sentry/helper';
import { useSessionStorage } from '@core/composables/use-storage';
import { FulfillmentOrderShippedItem } from '@monolith/legacy/types/fulfillment';
import { addDays, isBefore, startOfDay } from 'date-fns';
import { ShippingMethodUnavailableReason } from '@monolith/legacy/types/api/shipping';
import { BillingItem, OrderUpdates, SplitItem } from '@monolith/legacy/types/order-v2';
import { getItemsWithZeroQntFromOrderEvents } from '@monolith/legacy/store/legacy-account/legacy-account.utility';

const initialState: State = {
  cancellationReasonFilter: null,
  orders: [],
  orderDetail: null,
  fulfillmentOrderDetail: null,
  editedOrder: {},
  initialOrder: {},
  currentOrderPage: 1,
  orderPerPage: 5,
  lastPage: 1,
  totalOrderItems: 0,
  statusFilter: null,
  priorityFilter: null,
  typeOfOrderFilter: null,
  typeOfExternalOrderFilter: null,
  orderLoading: false,
  orderItemsLoading: false,
  orderFulfillmentLoading: false,
  affiliatedClients: [],
  currentAffiliatedClientsPage: 1,
  totalAffiliatedClients: 0,
  affiliatedClientsByPage: 5,
  showAlcoholWarning: false,
  searchTerm: '',
  countByStatus: {
    ankor_confirmed: null,
    invoiced: null,
  },
  countsByTypeOfOrder: {
    internal: null,
    external: null,
  },
  countOrderRestrictedByEORINumber: 0,
  //to be refactored after new router merge
  liked: {
    brands: {},
    products: {},
  },
  likedBrandsPagination: {
    loading: false,
    currentPage: 1,
    itemsPerPage: 24,
    totalItems: 0,
    itemsOrder: [],
    nbPage: 0,
    from: 1,
    to: 1,
    lastPage: 1,
  },
  likedProductsPagination: {
    loading: false,
    currentPage: 1,
    itemsPerPage: 36,
    totalItems: 0,
    itemsOrder: [],
  },
  orderItems: null,
};

const masterOrderFetchParams: GetMasterOrderParams = {
  include: ['orderShipping', 'orderIssue', 'brandRetailerRelationship', 'splitOff', 'splitFrom'],
  fields: {
    'order-shipping': ['shippingMethod', 'isAddingTrackingInformationPostponed', 'stockOrigin'],
    'split-orders': ['uuid', 'wrapperOrderInternalId'],
  },
};

export default {
  namespaced: true,
  state: initialState,
  actions: {
    async fetchLikedBrands({ commit, state }) {
      if (!isEnabled('like_products_and_brands')) {
        return;
      }
      const params: Params = {};

      commit('TOGGLE_BRANDS_LOADING', true);

      if (state.likedBrandsPagination.currentPage) {
        params.page = state.likedBrandsPagination.currentPage;
      }

      const { data } = await http().get('/api/likes/brands', { params, noProgressBar: true });
      if (data.data.length === 0) {
        this['_vm'].$emit('no-datas', data.meta.current_page);
      }
      commit(
        'SET_LIKED_BRANDS',
        data.data.reduce((object, value) => Object.assign(object, { [value.id]: value }), {})
      );
      commit(
        'SET_BRANDS_ORDER',
        data.data.map((item) => item.id)
      );
      commit('SET_BRANDS_CURRENT_PAGE', data.meta.current_page);
      commit('SET_BRANDS_NB_PAGE', Math.ceil(data.meta.total / data.meta.per_page));
      commit('SET_BRANDS_PER_PAGE', data.meta.per_page);
      commit('SET_TOTAL_BRANDS_ITEMS', data.meta.total);
      commit('TOGGLE_BRANDS_LOADING', false);
      commit('SET_BRANDS_FROM', data.meta.from);
      commit('SET_BRANDS_TO', data.meta.to);
      commit('SET_BRANDS_LAST_PAGE', data.meta.last_page);
    },
    changeCurrentLikedBrandPage({ commit, dispatch }, page) {
      commit('SET_BRANDS_CURRENT_PAGE', page);
      dispatch('fetchLikedBrands');
    },
    fetchLikedProducts({ commit, state }) {
      if (!isEnabled('like_products_and_brands')) {
        return;
      }
      const params: Params = {};

      commit('TOGGLE_LIKED_PRODUCTS_LOADING', true);

      if (state.likedProductsPagination.currentPage) {
        params.page = state.likedProductsPagination.currentPage;
      }

      http()
        .get('/api/likes/products', { params, noProgressBar: true })
        .then(({ data }) => {
          if (data.data.length === 0) {
            this['_vm'].$emit('no-datas', data.meta.current_page);
          }
          commit(
            'SET_LIKED_PRODUCTS',
            data.data.reduce((object, value) => Object.assign(object, { [value.id]: value }), {})
          );

          commit(
            'SET_LIKED_PRODUCTS_ORDERS',
            data.data.map((item) => item.id)
          );

          commit('SET_LIKED_PRODUCTS_CURRENT_PAGE', data.meta.current_page);
          commit('SET_LIKED_PRODUCTS_PER_PAGE', data.meta.per_page);
          commit('SET_TOTAL_LIKED_PRODUCTS_ITEMS', data.meta.total);
          commit('TOGGLE_LIKED_PRODUCTS_LOADING', false);
        });
    },
    changeCurrentLikedProductPage({ commit, dispatch }, page) {
      commit('SET_LIKED_PRODUCTS_CURRENT_PAGE', page);
      dispatch('fetchLikedProducts');
    },
    removeLikedProducts({ commit, state }, id) {
      commit('UNSET_LIKED_PRODUCT', id);
      commit(
        'SET_LIKED_PRODUCTS_ORDERS',
        state.likedProductsPagination.itemsOrder.filter((item) => item != id)
      );
    },
    async fetchOrders({ commit, state, rootGetters }): Promise<void> {
      let params: Params;
      if (rootGetters.userIsRetailer) {
        params = {
          'fields[orders]': 'internalId,createdAt,brand,orderItemCount,status,totals',
        };
      } else {
        params = {
          include: 'orderShipping,orderIssue',
          'fields[order-shipping]': 'shippingMethod,isAddingTrackingInformationPostponed,stockOrigin',
          'fields[order-issue]': 'trackingLink,willAddTrackingLater,status',
          'fields[orders]':
            'priorityTag,createdAt,deadlines,internalId,internalUuid,rejection,retailer,status,synchronizations,totals,tracking,type,orderShipping,fulfillmentReference,fulfillmentProvider,orderIssue,metadata,amounts,externalOrderType',
        };
      }

      commit('TOGGLE_ORDER_LOADING', true);

      if (state.currentOrderPage) {
        params['page[offset]'] = (state.currentOrderPage - 1) * state.orderPerPage;
      }

      if (state.statusFilter) {
        params['filter[status_key]'] = state.statusFilter;
      }

      if (
        state.priorityFilter &&
        (state.statusFilter === null ||
          [OrderStatuses.PENDING_VALIDATION, OrderStatuses.TO_SHIP, OrderStatuses.RECEPTION_REFUSED].includes(state.statusFilter))
      ) {
        params['filter[priority_key]'] = state.priorityFilter;
      }

      if (state.typeOfOrderFilter) {
        params['filter[order_type]'] = state.typeOfOrderFilter;
      }

      if (state.typeOfExternalOrderFilter) {
        params['filter[external_order_type]'] = state.typeOfExternalOrderFilter;
      }

      if (
        state.cancellationReasonFilter &&
        (state.statusFilter === null || [OrderStatuses.CANCELLED].includes(state.statusFilter))
      ) {
        params['filter[brand_cancellation_key]'] = state.cancellationReasonFilter;
      }

      if (state.searchTerm) {
        params['filter[search_term]'] = state.searchTerm;
      }

      params['page[limit]'] = 15;

      return http()
        .get<{
          data: OrderBasicDetail[];
          meta: {
            page: {
              currentPage: number;
              perPage: number;
              lastPage: number;
              total: number;
            };
          };
        }>(`/api/internal/v1/ordering/orders`, { params })
        .then((response) => {
          const deserializedResponse = deserialize(response.data);
          const orders = deserializedResponse.data;
          const meta = deserializedResponse.meta;
          commit('SET_USER_ORDERS', Object.freeze(orders));
          commit('SET_ORDER_CURRENT_PAGE', meta.page.currentPage);
          commit('SET_ORDER_PER_PAGE', meta.page.perPage);
          commit('SET_LAST_PAGE', meta.page.lastPage);
          commit('SET_TOTAL_ORDER_ITEMS', meta.page.total);
          commit('SET_COUNTS_BY_STATUS', meta.count_by_status_key);
          commit('SET_COUNTS_BY_TYPE_OF_ORDER', meta.count_by_type_of_order);
          commit('TOGGLE_ORDER_LOADING', false);
        });
    },
    rejectOrder(
      { commit, dispatch },
      { id, internalId, rejectType }: { id: string; internalId: number; rejectType: OrderBrandRejectionType }
    ) {
      commit('TOGGLE_ORDER_LOADING', true);

      return rejectOrder(internalId, rejectType)
        .catch(captureException)
        .finally(() => {
          dispatch('fetchOrder', id);
        });
    },
    async fetchOrder({ commit, dispatch }, id) {
      try {
        const order = await getMasterOrder(id, masterOrderFetchParams, { noProgressBar: true });

        dispatch('saveOrderDetail', {
          order: Object.freeze(order.data),
          counter: 1,
        });
        commit('TOGGLE_ORDER_LOADING', false);
      } catch (e) {
        captureEvent(e);
        commit('TOGGLE_ORDER_LOADING', false);
      }
    },
    async fetchFulfillmentOrder({ commit }, id) {
      try {
        const fulfillmentOrders = await getFulfillmentOrdersByMasterOrderUuid(id);

        if (fulfillmentOrders.data.length > 0) {
          commit('SET_FULFILLMENT_ORDER_DETAIL', fulfillmentOrders?.data?.[0]);
        }
      } catch (e) {
        captureFulfillmentException(e);
      }
    },
    async fetchOrderSilently({ dispatch }, { id, counter }) {
      const order = await getMasterOrder(id, masterOrderFetchParams, { noProgressBar: true });

      dispatch('saveOrderDetail', {
        order: Object.freeze(order.data),
        counter: counter,
      });
    },
    changeCurrentOrderPage({ commit, dispatch }, page): Promise<void> {
      commit('SET_ORDER_CURRENT_PAGE', page);
      return dispatch('fetchOrders');
    },
    resetAllFilters({ commit, dispatch }) {
      commit('CLEAR_PRIORITY_FILTER');
      commit('CLEAR_TYPE_OF_ORDER_FILTER');
      commit('CLEAR_CANCELLATION_REASON_FILTER');
      dispatch('changeCurrentOrderPage', 1);
    },
    resetOrderFilters({ commit, dispatch }) {
      commit('CLEAR_ORDER_FILTERS');
      dispatch('changeCurrentOrderPage', 1);
    },
    resetCancellationReasonFilter({ commit, dispatch }) {
      commit('CLEAR_CANCELLATION_REASON_FILTER');
      dispatch('changeCurrentOrderPage', 1);
    },
    resetPriorityFilter({ commit, dispatch }) {
      commit('CLEAR_PRIORITY_FILTER');
      dispatch('changeCurrentOrderPage', 1);
    },
    resetTypeOfOrderFilter({ commit, dispatch }) {
      commit('CLEAR_TYPE_OF_ORDER_FILTER');
      dispatch('changeCurrentOrderPage', 1);
    },
    changeFilterOrder({ commit, dispatch }, filter) {
      commit('SET_ORDER_FILTER', filter);
      dispatch('changeCurrentOrderPage', 1);
    },
    changeCancellationReasonFilter({ commit, dispatch }, filter) {
      commit('SET_CANCELLATION_REASON_FILTER', filter);
      dispatch('changeCurrentOrderPage', 1);
    },
    changeFilterPriorityOrder({ commit, dispatch }, priority) {
      commit('SET_ORDER_PRIORITY_FILTER', priority);
      dispatch('changeCurrentOrderPage', 1);
    },
    changeFilterTypeOfOrder({ commit, dispatch }, { typeOfOrder, externalTypeOfOrder }) {
      commit('SET_ORDER_TYPE_FILTER', typeOfOrder);
      commit('SET_EXTERNAL_ORDER_TYPE_FILTER', externalTypeOfOrder);
      dispatch('changeCurrentOrderPage', 1);
    },
    async saveOrderDetail({ commit, dispatch, state }, { order, counter }) {
      // If deprecated order model is trying to be saved (as it might come from an endpoint that still uses deprecated order model), request new model
      if (Number.isInteger(order.id)) {
        try {
          const id = await getMasterOrderUuidByInternalOrderId(order.id);
          const newPayload = await getMasterOrder(id, masterOrderFetchParams, { noProgressBar: true });
          dispatch('saveOrderDetail', {
            order: Object.freeze(newPayload.data),
            counter: 1,
          });
          commit('TOGGLE_ORDER_LOADING', false);
        } catch (e) {
          captureEvent(e);
          commit('TOGGLE_ORDER_LOADING', false);
        }

        return;
      }
      counter = counter || 1;
      if (
        counter <= 6 &&
        (order.status?.name === 'received' ||
          (order.status?.name === 'brand_confirmed' && isShippingViaAnkorstore(order.orderShipping?.data.shippingMethod)))
      ) {
        // Wait for the invoice generation
        setTimeout(() => {
          // if we change of orderDetail, we don't want the dispatch to occur.
          if (state.orderDetail && state.orderDetail.id === order.id) {
            ++counter;
            dispatch('fetchOrderSilently', { id: order.id, counter: counter });
          }
        }, 5000 * counter);
      }
      commit('SET_ORDER_DETAIL', order);
      if (order.items) {
        commit('SET_ORDER_ITEMS', order.items);
        commit('SET_INITIAL_ORDER', order.items);
      }
    },
    editItemOfOrder({ commit }, itemOrder) {
      commit('SET_ITEM_QUANTITY', itemOrder);
    },
    cancelEditOrder({ commit, dispatch, state }) {
      commit('RESET_EDIT_ORDER');
      dispatch('reloadWanted', state.orderDetail.id);
    },
    saveEditedOrder({ commit, state, dispatch, getters }) {
      if (!state.orderLoading) {
        commit('TOGGLE_ORDER_LOADING', true);
        const order_items = Object.values(state.editedOrder as EditedOrderItems).map((item) => ({
          order_item_id: getters.getItemProperty(item, 'internalId'),
          quantity: item.batchQuantity,
        }));
        http()
          .patch(`/api/orders/${state.orderDetail.internalId}/items`, {
            order_items,
          })
          .then(({ data }) => {
            dispatch('saveOrderDetail', {
              order: Object.freeze(data.data),
              counter: 1,
            });
            dispatch('reloadWanted', state.orderDetail.id);
          })
          .finally(() => {
            commit('TOGGLE_ORDER_LOADING', false);
          });
      }
    },
    saveEditedOrderPayOrder({ commit, state, dispatch }) {
      if (!state.orderLoading) {
        commit('TOGGLE_ORDER_LOADING', true);
        const masterOrderUuid = state.orderDetail.id;
        const items = Object.values(state.editedOrder as EditedOrderItems).map((item) => ({
          productVariantUuid: item.id,
          quantity: item.batchQuantity,
        }));
        http()
          .patch(
            `/api/internal/v1/order-pay/order-pay-orders/${state.orderDetail.externalOrderUuid}/-actions/quantities`,
            {
              data: {
                type: 'order-pay-orders',
                attributes: {
                  items,
                },
                id: state.orderDetail.externalOrderUuid,
              },
            },
            {
              headers: {
                'Content-Type': 'application/vnd.api+json',
                Accept: 'application/vnd.api+json',
              },
            }
          )
          .then(({ data }) => {
            dispatch('saveOrderDetail', {
              order: Object.freeze(data.data),
              counter: 1,
            });
            dispatch('reloadWanted', masterOrderUuid);
          })
          .finally(() => {
            commit('TOGGLE_ORDER_LOADING', false);
          });
      }
    },
    sendAction({ commit, dispatch }, { orderId, action, parameters, options }) {
      commit('TOGGLE_ORDER_LOADING', options?.toggleOrderLoading ?? true);
      const sessionStorage = useSessionStorage();
      let payload = { action };

      if (parameters) {
        payload = { ...parameters, ...payload };
      }

      return http()
        .post(`/api/orders/${orderId}/action`, payload)
        .then(async ({ data }) => {
          if (payload.action === ShipmentMethod.FULFILL_WITH_ANKORSTORE) {
            sessionStorage.setStorageItem(ORDER_AUTOMATION_MODAL_CONTEXT_SESSION_KEY, OrderAutomationModalContext.FULFILLMENT);
          }

          await dispatch('saveOrderDetail', { order: Object.freeze(data.data) });
          commit('TOGGLE_ORDER_LOADING', false);
          if (options?.reloadPage) {
            dispatch('reloadWanted', data.data.id);
          }
        });
    },
    fetchAffiliatedClients({ commit, state }) {
      const params: Params = {};

      commit('TOGGLE_ORDER_LOADING', true);

      if (state.currentAffiliatedClientsPage) {
        params.page = state.currentAffiliatedClientsPage;
      }

      http()
        .get(`/api/me/brand/affiliated-retailers`, { params })
        .then(({ data }) => {
          commit('SET_AFFILIATED_WITH_PAGINATION', {
            data: data.data,
            meta: data.meta,
          });
          commit('TOGGLE_ORDER_LOADING', false);
        });
    },
    changeCurrentAffiliationClientsPage({ commit, dispatch }, page) {
      commit('SET_AFFILIATED_CLIENT_CURRENT_PAGE', page);
      dispatch('fetchAffiliatedClients');
    },
    setSearchTerm({ commit, dispatch }, term: string) {
      commit('SET_SEARCH_TERM', term);
      return dispatch('changeCurrentOrderPage', 1);
    },
    clearSearchTerm({ commit, dispatch }) {
      commit('CLEAR_SEARCH_TERM');
      return dispatch('changeCurrentOrderPage', 1);
    },
    async fetchOrderItems({ getters, commit }, id) {
      commit('TOGGLE_ORDER_ITEMS_LOADING', true);

      if (Number.isInteger(id)) {
        id = await getMasterOrderUuidByInternalOrderId(id);
      }
      const { data } = await http().get(`/api/internal/v1/ordering/orders/${id}/order-items?include=orderedProduct`);
      const items: OrderItemEntity[] = deserialize(data).data;

      // extend items with item updates
      if (getters.orderHasUpdates) {
        const itemUpdates = getters.getOrderUpdate('itemUpdates');

        itemUpdates.forEach((itemUpdate: ItemUpdates) => {
          const itemToUpdate = items.find((item) => item.id === itemUpdate.itemId);
          if (itemToUpdate) {
            itemToUpdate.oldAmount = itemUpdate.oldAmount;
            itemToUpdate.oldMultipliedQuantity = itemUpdate.oldMultipliedQuantity;
            itemToUpdate.oldQuantity = itemUpdate.oldQuantity;
          }
        });
      }

      // extend items with events updates
      // Updates come from the order events payload.
      // We filter the events to get only the ones that are related to the items.
      if (getters.orderHasEvents) {
        const orderEvents: OrderUpdates[] = getters.orderEvents;
        items.forEach((item: OrderItemEntity) => {
          item.events = orderEvents
            .map((event) => {
              const itemOrderEvents = event.updates.itemUpdates.find((update) => update.itemId === item.id);
              if (itemOrderEvents) {
                return {
                  type: event.type,
                  ...itemOrderEvents,
                };
              }
              return null;
            })
            .filter((event) => event !== null);
        });
        // if we don't have any match it could mean that the updates sets the item to 0 so we need to add it to the list
        items.push(...getItemsWithZeroQntFromOrderEvents(orderEvents));
      }

      // Check if the order has splitOff data and the item has an event of type "order_split"
      const splitOffItem = getters.orderSplitOff;
      if (splitOffItem) {
        items.forEach((item) => {
          const splitEvent = item.events?.find((event) => event.type === OrderItemEntityEventsName.ORDER_SPLIT);
          if (splitEvent) {
            item.splitOrder = splitOffItem.wrapperOrderInternalId;
          }
        });
      }
      commit('SET_ORDER_ITEMS', items);
      commit('SET_INITIAL_ORDER', items);
      commit('TOGGLE_ORDER_ITEMS_LOADING', false);
    },
    async fetchInfoForRemovedItemsInOrder(_, itemsRemovedFromOrder: ItemUpdates[]) {
      const productsRemoved = await getSelectedProductsVariantByUuid(itemsRemovedFromOrder.map((item) => item.itemId));
      const itemsMissing: OrderItemEntity[] = [];

      productsRemoved.forEach((product) => {
        const currentItemToUpdate = itemsRemovedFromOrder.find((item) => item.itemId === product.variants[0].uuid);
        if (!currentItemToUpdate) {
          return;
        }
        const orderItem: OrderItemEntity = {
          type: ItemsType.ORDER_ITEMS,
          id: product.variants[0].uuid,
          orderedProduct: {
            data: {
              images: product.images,
              fulfillableId: product.variants[0].fulfillableId,
              name: product.name,
              options: product.variants[0].options,
              pageUrl: product.link,
              id: product.variants[0].uuid,
              type: product.type,
              sku: product.variants[0].sku,
            },
          },
          internalId: product.id,
          batchQuantity: 0,
          unitQuantity: 0,
          unitPrice: product.variants[0].price.wholesale_price,
          totalPrice: { amount: 0, currency: currentItemToUpdate.oldAmount.currency },
          oldAmount: currentItemToUpdate.oldAmount,
          oldMultipliedQuantity: currentItemToUpdate.oldMultipliedQuantity,
          oldQuantity: currentItemToUpdate.oldQuantity,
        };

        itemsMissing.push(orderItem);
      });

      return itemsMissing;
    },
    reloadWanted() {}, //Do nothing, the reload action is subscribed by component
    toggleOrderFulfillmentLoading({ commit }, value: boolean) {
      commit('TOGGLE_FULFILLMENT_ORDER_LOADING', value);
    },
  },
  mutations: {
    MUTATE_ITEM_PROPERTY(_, { item, property, value }) {
      item[property] = value;
    },
    TOGGLE_ORDER_LOADING(state, value) {
      state.orderLoading = value;
    },
    TOGGLE_ORDER_ITEMS_LOADING(state, value: boolean) {
      state.orderItemsLoading = value;
    },
    TOGGLE_FULFILLMENT_ORDER_LOADING(state, value) {
      state.orderFulfillmentLoading = value;
    },
    REINIT_ORDER(state) {
      state.orderDetail = null;
    },
    SET_ORDER_CURRENT_PAGE(state, page) {
      state.currentOrderPage = page;
    },
    SET_USER_ORDERS(state: State, orders: OrderBasicDetail[]) {
      state.orders = orders;
    },
    SET_ORDER_PER_PAGE(state, orderPerPage) {
      state.orderPerPage = orderPerPage;
    },
    SET_LAST_PAGE(state, lastPage) {
      state.lastPage = lastPage;
    },
    SET_TOTAL_ORDER_ITEMS(state, total) {
      state.totalOrderItems = total;
    },
    SET_ORDER_FILTER(state, filter) {
      state.statusFilter = filter;
    },
    SET_CANCELLATION_REASON_FILTER(state, filter) {
      state.cancellationReasonFilter = filter;
    },
    SET_ORDER_PRIORITY_FILTER(state, filter) {
      state.priorityFilter = filter;
    },
    SET_ORDER_TYPE_FILTER(state, filter) {
      state.typeOfOrderFilter = filter;
    },
    SET_EXTERNAL_ORDER_TYPE_FILTER(state, filter) {
      state.typeOfExternalOrderFilter = filter;
    },
    SET_ORDER_DETAIL(state, order: OrderDetail) {
      state.orderDetail = order;
      // whenever we update the order detail we also want to update the collection of order
      state.orders = [...state.orders.map((item) => (item.id !== order.id ? item : { ...item, ...order }))];
    },
    SET_FULFILLMENT_ORDER_DETAIL(state, filter) {
      state.fulfillmentOrderDetail = filter;
    },
    SET_COUNTS_BY_STATUS(state, counts) {
      state.countByStatus = counts;
    },
    SET_COUNTS_BY_TYPE_OF_ORDER(state, counts) {
      state.countsByTypeOfOrder = counts;
    },
    SET_COUNT_ORDER_RESTRICTED_BY_EORI_NUMBER(state, count) {
      state.countOrderRestrictedByEORINumber = count;
    },
    SET_AFFILIATED_CLIENT_CURRENT_PAGE(state, page) {
      state.currentAffiliatedClientsPage = page;
    },
    SET_AFFILIATED_WITH_PAGINATION(state, { data, meta }) {
      state.affiliatedClients = data;
      state.currentAffiliatedClientsPage = meta.current_page;
      state.totalAffiliatedClients = meta.total;
      state.affiliatedClientsByPage = meta.per_page;
    },
    SET_PRODUCT_CURRENT_PAGE(state, page) {
      state.currentProductPage = page;
    },
    SET_PRODUCT_ORGANIZATION_MODE(state, val) {
      state.productOrganizationMode = val;
    },
    SET_USER_PRODUCTS(state, products) {
      state.products = products;
    },
    SET_PRODUCT_NEW_ORDER(state, products) {
      state.productsReorderingNotPersisted = products;
    },
    SET_PRODUCT_PER_PAGE(state, productPerPage) {
      state.productPerPage = productPerPage;
    },
    SET_TOTAL_PRODUCT_ITEMS(state, total) {
      state.totalProductItems = total;
    },
    SET_PRODUCT_DETAIL(state, order) {
      state.productDetail = order;
    },
    UPDATE_PRODUCTS(state, products) {
      state.products = products;
    },
    SET_CHECKED_PRODUCTS(state, checked) {
      state.productsChecked = checked;
    },
    SET_SAVEBAR_DIRTY(state, value) {
      state.savebarDirty = value;
    },
    SET_SAVEBAR_TYPE(state, type) {
      state.saveBarType = type;
    },

    RESET_EDIT_ORDER(state) {
      state.editedOrder = { ...state.initialOrder };
    },
    SET_INITIAL_ORDER(state, items: OrderItemEntity[]) {
      items.forEach((item) => (state.initialOrder[item.id] = item));
      state.editedOrder = { ...state.initialOrder };
    },
    SET_ORDER_ITEMS(state, items: OrderItemEntity[]) {
      state.orderItems = items;
    },
    SET_ITEM_QUANTITY(state, itemOrder) {
      state.editedOrder[itemOrder.id] = { ...itemOrder };
    },
    SET_LIKED_BRANDS(state, brands) {
      state.liked.brands = brands;
    },
    SET_BRANDS_CURRENT_PAGE(state, page) {
      state.likedBrandsPagination.currentPage = page;
    },
    SET_BRANDS_PER_PAGE(state, itemsPerPage) {
      state.likedBrandsPagination.itemsPerPage = itemsPerPage;
    },
    SET_TOTAL_BRANDS_ITEMS(state, total) {
      state.likedBrandsPagination.totalItems = total;
    },
    SET_BRANDS_ORDER(state, itemsOrder) {
      state.likedBrandsPagination.itemsOrder = itemsOrder;
    },
    TOGGLE_BRANDS_LOADING(state, value) {
      state.likedBrandsPagination.loading = value;
    },
    SET_BRANDS_NB_PAGE(state, value) {
      state.likedBrandsPagination.nbPage = value;
    },
    SET_BRANDS_FROM(state, value) {
      state.likedBrandsPagination.from = value;
    },
    SET_BRANDS_TO(state, value) {
      state.likedBrandsPagination.to = value;
    },
    SET_BRANDS_LAST_PAGE(state, value) {
      state.likedBrandsPagination.lastPage = value;
    },
    SET_LIKED_PRODUCTS(state, products) {
      state.liked.products = products;
    },
    UNSET_LIKED_PRODUCT(state, id) {
      delete state.liked.products[id];
    },
    SET_TOTAL_LIKED_PRODUCTS_ITEMS(state, total) {
      state.likedProductsPagination.totalItems = total;
    },
    SET_LIKED_PRODUCTS_ORDERS(state, itemsOrder) {
      state.likedProductsPagination.itemsOrder = itemsOrder;
    },
    TOGGLE_LIKED_PRODUCTS_LOADING(state, value) {
      state.likedProductsPagination.loading = value;
    },
    SET_SHOW_ALCOHOL_WARNING(state, value) {
      state.showAlcoholWarning = value;
    },
    SET_SEARCH_TERM(state, term: string): void {
      state.searchTerm = term;
    },
    CLEAR_SEARCH_TERM(state: State): void {
      state.searchTerm = '';
    },
    CLEAR_ORDER_FILTERS(state: State): void {
      state.searchTerm = '';
      state.priorityFilter = null;
      state.typeOfOrderFilter = null;
      state.statusFilter = null;
    },
    CLEAR_PRIORITY_FILTER(state: State): void {
      state.priorityFilter = null;
    },
    CLEAR_TYPE_OF_ORDER_FILTER(state: State): void {
      state.typeOfOrderFilter = null;
    },
    CLEAR_CANCELLATION_REASON_FILTER(state: State): void {
      state.cancellationReasonFilter = null;
    },
    CLEAR_STATUS_FILTER(state: State): void {
      state.statusFilter = null;
    },
  },
  getters: {
    getOrders: (state: State): OrderBasicDetail[] => state.orders,
    getOrderDetail: (state) => state.orderDetail,
    getOrderItems: (state) => state.orderItems,
    getNumberOfBrandsLikedPagination: (state) => state.likedBrandsPagination.nbPage,
    getLikedBrands: (state) => state.liked.brands,
    getLikedBrandById: (_state, getters) => (id) => getters.getLikedBrands[id],
    getLikedBrandsIdsOrder: (state) => state.likedBrandsPagination.itemsOrder,
    getOrderedLikedBrands: (_state, getters) => getters.getLikedBrandsIdsOrder.map((id) => getters.getLikedBrandById(id)),
    isLikedBrandsLoading: (state) => state.likedBrandsPagination.loading,
    isOrderLoading: (state) => state.orderLoading,
    isOrderFulfillmentLoading: (state) => state.orderFulfillmentLoading,
    isOrderItemsLoading: (state) => state.orderItemsLoading,
    countByStatus: (state) => (status) => state.countByStatus[status] || 0,
    countsByTypeOfOrder: (state) => (status) => state.countsByTypeOfOrder[status] || 0,
    countOrderRestrictedByEORINumber: (state) => state.countOrderRestrictedByEORINumber || 0,
    getOrderEditedQuantity: (state) => (id) => {
      return state.editedOrder[id]?.batchQuantity;
    },
    isOrderEdited: (state) => JSON.stringify(state.editedOrder) !== JSON.stringify(state.initialOrder),
    isOrderWithFastPayment: (_state: State, getters): boolean => {
      return getters.orderBillingItems?.some((item) => item.type === 'brand_fast_payment');
    },
    orderSource: (state) => {
      return state.orderDetail?.source;
    },
    orderStatusName: (state) => {
      return state.orderDetail?.status?.name;
    },
    orderStatusTitle: (state) => {
      return state.orderDetail?.status?.title;
    },
    orderStatusDetails: (state) => {
      return state.orderDetail?.status?.details;
    },
    orderEstimatedDeliveryDates(state) {
      return state.orderDetail?.estimated_delivery_dates;
    },
    orderShippingPickup(state) {
      const pickup = state.orderDetail?.shipping?.shipment?.pickup;

      if (pickup) {
        return {
          date: pickup.date,
          closeTime: pickup.closeTime,
          readyTime: pickup.readyTime,
        };
      }
      return null;
    },
    orderShippingFromAddress(state) {
      const address = state.orderDetail?.shipping?.shipment?.shipFromAddress;
      return {
        contactPerson: address.contactPerson.fullName,
        companyName: address.company,
        addressLine: address.address.addressLine,
        postalCode: address.address.postalCode,
        city: address.address.city,
        countryCode: address.address.countryCode,
      };
    },
    orderShowAlcoholWarning(state) {
      return state.orderDetail?.meta?.showAlcoholWarning;
    },
    orderShowManualInvoiceDownloadWarning(state) {
      return state.orderDetail?.meta?.orderShowManualInvoiceDownloadWarning;
    },
    orderIsInternationalShipping(state) {
      return state.orderDetail?.shipping?.isInternational;
    },
    orderDeadlineAccept: (state) => {
      return state.orderDetail?.deadlines?.toAccept;
    },
    orderDeadlineShipping: (state) => {
      return state.orderDetail?.deadlines?.toShip;
    },
    orderUpdatedDeadlineShipping: (state) => {
      return state.orderDetail?.deadlines?.updatedToShip;
    },
    orderTrackingLink: (state) => {
      return state.orderDetail?.tracking?.trackingLink;
    },
    orderTrackingNumber: (state) => {
      return state.orderDetail?.tracking?.trackingNumber;
    },
    orderTrackingStatus: (state) => {
      return state.orderDetail?.tracking?.status;
    },
    orderIsTracked: (state) => {
      return state.orderDetail?.tracking?.trackingLink && state.orderDetail?.tracking?.trackingNumber;
    },
    orderShippingProvider: (state) => {
      return state.orderDetail?.shipping?.shippingProvider;
    },
    orderShippedAt: (state, _getters, _rootState, rootGetters) => {
      return rootGetters.userIsRetailer
        ? state.orderDetail?.shipping?.shippedAt
        : state.orderDetail?.shipping?.shipment?.shippedAt;
    },
    getOrderBusinessModel: (state) => {
      return state.orderDetail?.meta?.businessModel;
    },
    isRetailerCancellationRequestOpened(state) {
      return state.orderDetail?.rejection?.isRetailerCancellationRequestOpened;
    },
    orderIsDiscovery(state) {
      return state.orderDetail?.isDiscovery;
    },
    orderIsDiscoveryForRetailer(state) {
      return state.orderDetail?.isDiscoveryForRetailer;
    },
    orderSplitOff(state): SplitItem {
      return state.orderDetail?.splitOff?.data;
    },
    orderSplitFrom(state): SplitItem {
      return state.orderDetail?.splitFrom?.data;
    },
    isLiftOrder(state: State): boolean {
      return state.orderDetail?.brandRetailerRelationship?.data?.type === BrandRetailerRelationshipType.LIFT || false;
    },
    isMyNetworkOrder(state: State) {
      return state.orderDetail?.brandRetailerRelationship?.data?.type === BrandRetailerRelationshipType.NETWORK || false;
    },
    getBrandFlatShippingFee(state: State): BillingItem {
      return state.orderDetail?.billing?.billingItems.find((item) => item.type === BillingItemType.brandFlatShippingFees);
    },
    getBrandShippingFeeRefund(state: State): BillingItem {
      return state.orderDetail?.billing?.billingItems.find((item) => item.type === BillingItemType.brandShippingFeesRefund);
    },
    orderFranco(_, getters): MasterOrderBillingItem['francoSetting'] {
      const flatFee = getters.orderBillingItems?.find(({ type }) => type === BillingItemType.brandFlatShippingFees);

      return flatFee ? getters.getBillingItemProperty(flatFee, 'francoSetting') : undefined;
    },
    orderIsAboveFranco(_, getters) {
      const franco = getters.orderFranco?.francoAmount;
      const orderTotal = getters.getOrderTotal('totalAmount');

      if (!franco) {
        return false;
      }

      return orderTotal.amount > franco.amount;
    },
    orderShippingCollectionMethod(state) {
      return state.orderDetail?.shipping?.shipmentEstimation?.collectionMethods;
    },
    getOrderBrandProperty: (state) => (property) => {
      const brandData = state.orderDetail?.brand;
      if (!brandData) {
        return null;
      }
      switch (property) {
        case 'id':
          return brandData.id;
        case 'name':
          return brandData.name;
        case 'link':
          return brandData.link;
        case 'images':
          return brandData.images;
        case 'city':
          return brandData.display_address?.city;
      }
    },
    getOrderRetailerProperty: (state) => (property) => {
      if (!state.orderDetail) {
        return null;
      }
      let retailerData;
      if (state.orderDetail.externalOrderType === ExternalOrderTypeOptions.ORDERPAY) {
        retailerData = state.orderDetail.metadata;
      } else {
        retailerData = state.orderDetail.retailer;
      }

      if (!retailerData) {
        return null;
      }
      switch (property) {
        case 'name':
          return retailerData.name;
        case 'id':
          return retailerData.id;
        case 'uuid':
          return retailerData.uuid;
        case 'phone':
          return state.orderDetail.externalOrderType === ExternalOrderTypeOptions.ORDERPAY
            ? retailerData.contactPerson.phone
            : retailerData.phone_number;
        case 'isoCode':
          return retailerData.business?.country.iso_code;
        case 'email':
          return state.orderDetail.externalOrderType === ExternalOrderTypeOptions.ORDERPAY
            ? retailerData.contactPerson.email
            : retailerData.email;
        case 'firstName':
          return retailerData.business?.user_first_name || retailerData.name;
        case 'lastName':
          return retailerData.business?.user_last_name || '';
        case 'vatNumber':
          return retailerData.business?.vat_number;
        case 'taxNumber':
          return retailerData.business?.tax_number;
        case 'city':
          return retailerData.city;
        case 'storeUrl':
          return retailerData.store_url;
        case 'deliverySchedule':
          return state.orderDetail.externalOrderType === ExternalOrderTypeOptions.NAFO ? retailerData.deliverySchedule : null;
        case 'deliveryAppointment':
          return state.orderDetail.externalOrderType === ExternalOrderTypeOptions.NAFO ? retailerData.deliveryAppointment : null;
      }
    },
    fulfillmentOrderClientType(state) {
      return state.fulfillmentOrderDetail?.recipientType;
    },
    fulfillmentOrderShippedItems(state): FulfillmentOrderShippedItem[] {
      return state.fulfillmentOrderDetail?.shippedItems;
    },
    orderType(state) {
      return state.orderDetail?.type;
    },
    isOrderSplittable(state: State) {
      return state.orderDetail?.meta?.isSplitAllowed;
    },
    externalOrderType(state) {
      return state.orderDetail?.externalOrderType;
    },
    orderPayOrderType(state) {
      return state.orderDetail?.externalOrderType === ExternalOrderTypeOptions.ORDERPAY;
    },
    orderStockOrigin(state) {
      return state.orderDetail?.orderShipping?.data?.stockOrigin;
    },
    getRetailerCountryIsoCode(state: State) {
      return state.orderDetail?.retailer.business.country.iso_code;
    },
    orderShippingMethod(state) {
      return state.orderDetail?.orderShipping?.data?.shippingMethod;
    },
    orderHasUpdates(state) {
      return state.orderDetail?.updates;
    },
    orderHasEvents(state) {
      return state.orderDetail?.events?.length > 0;
    },
    orderEvents(state: State): OrderUpdates[] {
      return state.orderDetail?.events;
    },
    orderShippingLabelGenerationError(state) {
      return state.orderDetail?.shipping?.hasShippingLabelGenerationError;
    },
    getAnkorlogisticsAvailability(state: State) {
      return state.orderDetail?.shipping?.isAnkorlogisticsAvailable;
    },
    orderShippingFeesRefund(state) {
      return state.orderDetail?.shipping?.fees?.refundAmount;
    },
    orderBilledWeightFee(state) {
      return state.orderDetail?.shipping?.billedWeightFee;
    },
    orderShipmentEstimationRateAmount(state) {
      return state.orderDetail?.shipping?.shipmentEstimation?.rateAmount;
    },
    orderTemporaryParcels(state) {
      return state.orderDetail?.shipping?.shipmentEstimation?.estimatedParcels;
    },
    orderCanBeShippedWithShippingLabels(state) {
      return !!state.orderDetail?.shipping?.availableShippingMethods?.find(
        (m) => m.name === 'ankorstore' && m.canBeUsed === true
      );
    },
    orderAksShippingEligibilityErrors(state) {
      return (
        state.orderDetail?.shipping?.availableShippingMethods?.find((m) => m.name === 'ankorstore' && m.canBeUsed === false)
          ?.errors || []
      );
    },
    isIneligibleForAksShippingBecauseHsCodeMissing(_, getters): boolean {
      return !!getters.orderAksShippingEligibilityErrors.find(
        ({ code }) => code === ShippingMethodUnavailableReason.InternationalShippingProductsWithoutHSCode
      );
    },
    isIneligibleForAksShippingBecauseMadeInCountryMissing(_, getters): boolean {
      return !!getters.orderAksShippingEligibilityErrors.find(
        ({ code }) => code === ShippingMethodUnavailableReason.InternationalShippingProductsWithoutMadeIn
      );
    },
    orderShippingLabelError(state) {
      return state.orderDetail?.shipping?.availableShippingMethods?.find((m) => m.name === 'ankorstore' && m.canBeUsed === false)
        ?.errors?.[0];
    },
    orderShippingAddress(state) {
      const res = {
        name: null,
        street: null,
        city: null,
        isoCode: null,
        organisationName: null,
        postalCode: null,
        countryName: null,
      };

      if (!state.orderDetail) {
        return res;
      }

      if (state.orderDetail.externalOrderType === ExternalOrderTypeOptions.ORDERPAY) {
        const { contactPerson, address } = state.orderDetail.metadata;
        res.name = contactPerson?.firstName + '  ' + contactPerson?.lastName;
        res.street = address?.addressLine;
        res.city = address?.city;
        res.isoCode = address?.countryCode;
        res.countryName = address?.countryCode;
        res.organisationName = address?.company;
        res.postalCode = address?.postalCode;
      } else if (state.orderDetail.shipping?.shippingAddress) {
        const { contactPerson, address, company } = state.orderDetail.shipping.shippingAddress;
        res.name = contactPerson?.fullName;
        res.street = address?.addressLine;
        res.city = address?.city;
        res.isoCode = address?.countryCode;
        res.countryName = address?.countryCode;
        res.organisationName = company;
        res.postalCode = address?.postalCode;
      }
      return res;
    },
    orderInvoices(state) {
      type Invoice = {
        isGenerated: null;
        number: null;
        title: null;
        path: null;
      };
      let res: Invoice[] = [];
      res = state.orderDetail?.invoices?.map((invoice) => {
        return {
          title: invoice.title,
          number: invoice.number,
          path: invoice.path,
          isGenerated: invoice.isGenerated,
        };
      });
      return res || [];
    },
    orderReference(state, getters) {
      return getters.orderType === 'internal' ? state.orderDetail?.internalId : state.orderDetail?.reference;
    },
    orderFulfillmentReference(state) {
      return state.orderDetail?.fulfillmentReference;
    },
    orderFulfillmentProvider(state) {
      return state.orderDetail?.fulfillmentProvider ?? null;
    },
    orderCreationDate(state) {
      return state.orderDetail?.createdAt;
    },
    orderId(state) {
      return state.orderDetail?.id;
    },
    checkoutId(state) {
      return state.orderDetail.checkoutId;
    },
    externalPaymentUuid(state) {
      return state.orderDetail?.externalPaymentUuid;
    },
    orderItemCount(state) {
      return state.orderDetail.orderItemCount;
    },
    orderInternalId(state) {
      return state.orderDetail?.internalId;
    },
    orderInternalUuid(state) {
      return state.orderDetail?.internalUuid;
    },
    orderExternalId(state) {
      return state.orderDetail?.externalOrderUuid;
    },
    orderFreeShippingEndDate(state) {
      return state.orderDetail?.freeShippingEndDate;
    },
    isReferralOrder(_state, getters) {
      const isOrderSubmittedOnTime = isBefore(
        startOfDay(new Date(getters.orderCreationDate)),
        startOfDay(addDays(new Date(getters.orderFreeShippingEndDate), 1))
      );
      return !!(getters.orderFreeShippingEndDate && isOrderSubmittedOnTime);
    },
    orderIssue(state) {
      return state.orderDetail?.orderIssue?.data;
    },
    orderItems(state: State): OrderItemEntity[] {
      return state.orderItems;
    },
    orderPriorityTag(state) {
      return state.orderDetail?.priorityTag;
    },
    orderBillingItems(state) {
      return state.orderDetail?.billing?.billingItems;
    },
    orderCustomReference(state) {
      return state.orderDetail.customReference;
    },
    getItemProperty: () => (item, property) => {
      if (!item) {
        return;
      }
      switch (property) {
        case 'id':
          return item.id;
        case 'internalId':
          return item.internalId;
        case 'quantity':
          return item.batchQuantity;
        case 'orderItemId':
          return item.orderItemId;
        case 'productId':
          return item.orderedProduct.data?.id;
        case 'hsCode':
          return item.orderedProduct.data?.hsCode;
        case 'madeInCountry':
          return item.orderedProduct.data?.madeInCountry;
        case 'images':
          return item.orderedProduct.data?.images;
        case 'unitQuantity':
          return item.unitQuantity;
        case 'unitPrice':
          return item.unitPrice;
        case 'totalPrice':
          return item.totalPrice;
        case 'newQuantity':
          return item.newQuantity;
        case 'oldQuantity':
          return item.oldQuantity;
        case 'name':
          return item.orderedProduct.data?.name;
        case 'originalName':
          return item.orderedProduct.data?.originalName;
        case 'sku':
          return item.orderedProduct.data?.sku;
        case 'product':
          return item.orderedProduct;
        case 'link':
          return item.orderedProduct.data?.pageUrl;
        case 'oldMultipliedQuantity':
          return item.oldMultipliedQuantity;
        case 'oldAmount':
          return item.oldAmount;
        case 'fulfillableId':
          return item.orderedProduct?.data?.fulfillableId;
        case 'variation':
          return item.orderedProduct.data?.options[0]?.value;
        case 'storageTemperature':
          return item.orderedProduct.data?.storageTemperature;
      }
    },
    getOrderUpdate: (state, getters) => (update) => {
      if (!getters.orderHasUpdates) {
        return;
      }
      switch (update) {
        case 'oldTotalAmount':
          return state.orderDetail.updates.oldTotalAmount;
        case 'oldTotalAmountVat':
          return state.orderDetail.updates.oldTotalAmountVat;
        case 'oldTotalAmountWithVat':
          return state.orderDetail.updates.oldTotalAmountWithVat;
        case 'oldRecargoAmount':
          return state.orderDetail.updates.oldRecargoAmount;
        case 'itemUpdates':
          return state.orderDetail.updates.itemUpdates;
      }
    },

    getOrderTotal: (state) => (total) => {
      // The second part is about OrderPay orders, when we have another payload.
      switch (total) {
        case 'totalAmount':
          return state.orderDetail?.totals?.totalAmount || state.orderDetail?.amounts?.brandTotalAmount;
        case 'totalAmountVat':
          return state.orderDetail?.totals?.totalVatAmount || state.orderDetail?.amounts?.brandTotalAmountVat;
        case 'totalAmountWithVat':
          return state.orderDetail?.totals?.totalAmountWithVat || state.orderDetail?.amounts?.brandTotalAmountWithVat;
        case 'netAmount':
          return state.orderDetail?.totals?.netAmount || state.orderDetail?.amounts?.brandTotalAmountWithVat;
        case 'retailerTotalAmountBeforeDiscount':
          return state.orderDetail?.totals?.retailerTotalAmountBeforeDiscount;
      }
    },
    getOrderVatInfo: (state) => (property) => {
      switch (property) {
        case 'isShifted':
          return state.orderDetail?.vat?.isShifted;
        case 'message':
          return state.orderDetail?.vat?.applicationDetails?.message;
        case 'link':
          return state.orderDetail?.vat?.applicationDetails?.link;
      }
    },
    getBillingItemProperty: () => (billingItem, property) => {
      switch (property) {
        case 'id':
          return billingItem.id;
        case 'amount':
          return billingItem.amount;
        case 'amountVat':
          return billingItem.amountVat;
        case 'label':
          return billingItem.label;
        case 'vatLabel':
          return billingItem.vatLabel;
        case 'isVatShifted':
          return billingItem.isVatShifted;
        case 'type':
          return billingItem.type;
        case 'displayVat':
          return billingItem.displayVat;
        case 'francoSetting':
          return billingItem.francoSetting;
      }
    },
    getOrderExtraTax: (state) => (tax) => {
      switch (tax) {
        case 'recargoDeEquivalencia':
          return state.orderDetail?.taxes?.find((t) => t.name === 'recargoDeEquivalencia')?.amount;
      }
    },
    orderRejectType(state) {
      return state.orderDetail?.rejection?.brandRejectType;
    },
    orderHasIssue(state) {
      return state.orderDetail?.issue?.current;
    },
    orderHasRevertedShippingLabels(state) {
      return state.orderDetail?.shipping?.hasRevertedShippingLabels;
    },
    orderShipment(state) {
      return state.orderDetail?.shipping?.shipment;
    },
    retailerCanOpenCancellationRequest(state) {
      return state.orderDetail.cancellation?.retailerCanOpenCancellationRequest;
    },
    orderShipmentTrackedPackages(state) {
      return state.orderDetail?.shipping?.shipment?.trackedPackages;
    },
    orderShipmentTrackedPackagesMergedLabelUrl(state) {
      return state.orderDetail?.shipping?.shipment?.trackedPackagesMergedLabelUrl;
    },
    orderLabelFormat(state) {
      return state.orderDetail?.shipping?.shipment?.labelPrintingFormat;
    },
    issueCaseVisibleToBrand: (state) => (issueCase) => {
      return state.orderDetail?.meta?.casesVisibleToBrand?.includes(issueCase);
    },
    issueCaseAllowedToBeSendToBrand: (state) => (issueCase) => {
      return state.orderDetail?.meta?.casesAllowedToBeSendToBrand?.includes(issueCase);
    },
    publicAppSynch(state) {
      let syncData = state.orderDetail?.synchronizations;
      syncData = syncData?.find((sync) => sync.type === 'publicApp');
      return syncData;
    },
    hasPublicAppSynch(_state, getters) {
      return getters.publicAppSynch !== undefined;
    },
    publicAppSynchName(_state, getters) {
      return getters.publicAppSynch?.platformName;
    },
    publicAppSynchError(_state, getters) {
      return getters.publicAppSynch?.error;
    },
    orderWillAddTrackingLater(state) {
      return state.orderDetail?.orderShipping?.data.isAddingTrackingInformationPostponed;
    },
    availableRejectReasons(state) {
      return state.orderDetail?.rejection?.availableBrandRejectReasons;
    },
    availableShippingLabelRevertReasons(state) {
      return state.orderDetail?.shipping?.shipment?.labelRevertReasons;
    },
  },
};
